import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
// import { Starry, StarryOptions } from "./Starry";
import { Dots, DotsOptions } from "./Dots";
import GUI from "three/examples/jsm/libs/lil-gui.module.min.js";
import { Flyline, FlylineOptions } from './Flyline';
import { cityList, bizLines } from '../config/flyline-config';
import { deepMerge } from "./common.util";

/** Earth 配置选项接口 */
export interface EarthOptions {
  /** 地球半径 */
  earthRadius?: number;
  /** 相机配置 */
  camera?: {
    /** 视角 */
    fov?: number;
    /** 近裁剪面 */
    near?: number;
    /** 远裁剪面 */
    far?: number;
    /** 相机位置 */
    position?: {
      x: number;
      y: number;
      z: number;
    };
  };
  /** 地球位置 */
  position?: {
    x: number;
    y: number;
    z: number;
  };
  /** 地球材质 */
  material?: {
    /** 颜色 */
    color?: number;
    /** 是否透明 */
    transparent?: boolean;
    /** 透明度 */
    opacity?: number;
    /** 粗糙度 */
    roughness?: number;
    /** 金属度 */
    metalness?: number;
  };
  /** 光照配置 */
  lights?: {
    /** 点光源 */
    pointLight?: {
      color?: number;
      intensity?: number;
      distance?: number;
      position?: {
        x: number;
        y: number;
        z: number;
      };
    };
    /** 半球光 */
    hemisphereLight?: {
      skyColor?: number;
      groundColor?: number;
      intensity?: number;
      position?: {
        x: number;
        y: number;
        z: number;
      };
    };
  };
  /** 控制器配置 */
  controls?: {
    /** 是否自动旋转 */
    autoRotate?: boolean;
    /** 自动旋转速度 */
    autoRotateSpeed?: number;
    /** 是否启用阻尼 */
    enableDamping?: boolean;
    /** 是否启用旋转 */
    enableRotate?: boolean;
    /** 是否启用平移 */
    enablePan?: boolean;
    /** 是否启用缩放 */
    enableZoom?: boolean;
    /** 最小极角 */
    minPolarAngle?: number;
    /** 最大极角 */
    maxPolarAngle?: number;
  };
}

export class Earth {
  /** 默认配置 */
  private static readonly DEFAULT_OPTIONS: Required<EarthOptions> = {
    earthRadius: 19.5,
    camera: {
      fov: 30,
      near: 1,
      far: 1000,
      position: {
        x: -40,
        y: 0,
        z: 0
      }
    },
    position: {
      x: 19.5,
      y: 0,
      z: 0
    },
    material: {
      color: 0x162a4e,
      opacity: 0.9,
      roughness: 0.3,
      metalness: 0.2
    },
    lights: {
      pointLight: {
        color: 0x081b26,
        intensity: 17,
        distance: 200,
        position: {
          x: -50,
          y: 0,
          z: 60
        }
      },
      hemisphereLight: {
        skyColor: 0xffffff,
        groundColor: 0x080820,
        intensity: 6,
        position: {
          x: 0,
          y: 0.1,
          z: -0.1
        }
      }
    },
    controls: {
      autoRotate: true,
      autoRotateSpeed: 1.2,
      enableDamping: true,
      enableRotate: true,
      enablePan: false,
      enableZoom: false,
      minPolarAngle: Math.PI / 2 - 0.5,
      maxPolarAngle: Math.PI / 2 + 0.5
    }
  };

  private renderDomElement: HTMLElement;
  private width: number;
  private height: number;
  /**
   * @description 渲染器
   */
  private renderer: THREE.WebGLRenderer;
  /**
   * @description 相机
   */
  private camera: THREE.PerspectiveCamera;
  /**
   * @description 场景
   */
  private scene: THREE.Scene;
  /**
   * @description 控制器
   */
  private controls: OrbitControls;
  /**
   * @description 星空
   */
  // private starry: Starry | undefined;
  /**
   * @description 点状地图
   */
  private dots: Dots | undefined;
  private flyline: Flyline | undefined;

  private resizeObserver: ResizeObserver;

  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private earthMesh: THREE.Mesh; // 存储地球网格的引用

  constructor(
    domElement: HTMLElement,
    private readonly options: Partial<EarthOptions> = {}
  ) {
    // 使用深度合并替换浅合并
    this.options = deepMerge(Earth.DEFAULT_OPTIONS, options);

    /**
     * @description 画布
     */
    this.renderDomElement = domElement;

    /**
     * @description 获取画布宽高
     */
    this.width = domElement.clientWidth;
    this.height = domElement.clientHeight;

    /**
     * @description 创建渲染器
     */
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(this.width, this.height);
    this.renderer.setClearColor(14540253, 0);
    this.renderer.sortObjects = false;
    domElement.appendChild(this.renderer.domElement);

    /**
     * @description 创建相机
     */
    this.camera = new THREE.PerspectiveCamera(
      /*  视角 */
      this.options.camera?.fov || 30,
      /* 宽高比 */
      this.width / this.height,
      /* 近裁剪面 */
      this.options.camera?.near || 1,
      /* 远裁剪面 */
      this.options.camera?.far || 1000
    );
    this.camera.position.set(this.options.camera?.position?.x || -80, this.options.camera?.position?.y || 80, this.options.camera?.position?.z || -30);
    this.camera.lookAt(this.options.camera?.position?.x || -80, this.options.camera?.position?.y || 80, this.options.camera?.position?.z || -30);

    /**
     * @description 创建场景
     */
    this.scene = new THREE.Scene();
    // 设置场景的背景颜色
		// 0x020924是一个深蓝色的十六进制颜色值
		// 这个颜色模拟了夜空的颜色，为地球模型创造了一个太空般的环境
		// this.scene.background = new THREE.Color(0x020924);
		// 为场景添加雾效果
		// 参数说明：
		// 第一个参数0x020924：雾的颜色，与背景颜色相同
		// 第二个参数200：雾效果开始的最小距离（从相机位置开始计算）
		// 第三个参数1000：雾效果完全不透明的最大距离
		// 这个效果会让远处的物体逐渐消失在背景中，增加场景的深度感
		// 在这个地球可视化项目中，雾效果可以让星空背景看起来更加自然，远处的星星会逐渐融入背景中
		// this.scene.fog = new THREE.Fog(0x020924, 200, 1000);
    if (import.meta.env.DEV) {
      // 创建一个坐标轴辅助器，红色为X轴，绿色为Y轴，蓝色为Z轴
      const axes = new THREE.AxesHelper(1000);
      this.scene.add(axes);
    }

    /**
     * @description 创建控制器
     */
    this.controls = new OrbitControls(this.camera, this.renderDomElement);
    /** 设置控制器自动旋转 */
		this.controls.autoRotate = this.options.controls?.autoRotate || true;
    /** 设置控制器自动旋转速度 */
		this.controls.autoRotateSpeed = this.options.controls?.autoRotateSpeed || 1.2;
    /** 设置控制器启用阻尼 */
		this.controls.enableDamping = this.options.controls?.enableDamping || true;
    /** 设置控制器启用旋转 */
		this.controls.enableRotate = this.options.controls?.enableRotate || true;
    /** 设置控制器启用平移 */
		this.controls.enablePan = this.options.controls?.enablePan || false;
    /** 设置控制器启用缩放 */
		this.controls.enableZoom = this.options.controls?.enableZoom || false;
    /** 设置控制器最小极角 */
		this.controls.minPolarAngle = this.options.controls?.minPolarAngle || Math.PI / 2 - 0.5;
    /** 设置控制器最大极角 */
		this.controls.maxPolarAngle = this.options.controls?.maxPolarAngle || Math.PI / 2 + 0.5;

    /**
     * @description 创建光照
     */
    const pointLight = new THREE.PointLight(this.options.lights?.pointLight?.color || 0x081b26, this.options.lights?.pointLight?.intensity || 17, this.options.lights?.pointLight?.distance || 200);
		pointLight.position.set(this.options.lights?.pointLight?.position?.x || -50, this.options.lights?.pointLight?.position?.y || 0, this.options.lights?.pointLight?.position?.z || 60);
		this.scene.add(pointLight);

    const hemisphereLight = new THREE.HemisphereLight(this.options.lights?.hemisphereLight?.skyColor || 0xffffff, this.options.lights?.hemisphereLight?.groundColor || 0x080820, this.options.lights?.hemisphereLight?.intensity || 6);
    hemisphereLight.position.set(this.options.lights?.hemisphereLight?.position?.x || 0, this.options.lights?.hemisphereLight?.position?.y || 0.1, this.options.lights?.hemisphereLight?.position?.z || -0.1);
		this.scene.add(hemisphereLight);

    // 如果需要调试光源位置
    if (import.meta.env.DEV) {
      const gui = new GUI();

      // 调试第一个点光源
      const pointLightHelper = new THREE.PointLightHelper(pointLight, 1);
      this.scene.add(pointLightHelper);

      // 添加点光源的调试
      const pointLightFolder = gui.addFolder('pointLight');
      pointLightFolder.add(pointLight.position, 'x', -100, 100);
      pointLightFolder.add(pointLight.position, 'y', -100, 100);
      pointLightFolder.add(pointLight.position, 'z', -100, 100);
      pointLightFolder.add(pointLight, 'intensity', 0, 10);
      pointLightFolder.add(pointLight, 'decay', 0, 10);

      // 添加半球光的调试
      const hemisphereLightHelper = new THREE.HemisphereLightHelper(hemisphereLight, 5);
      this.scene.add(hemisphereLightHelper);

      const hemisphereLightFolder = gui.addFolder('hemisphereLight');
      hemisphereLightFolder.add(hemisphereLight.position, 'x', -100, 100);
      hemisphereLightFolder.add(hemisphereLight.position, 'y', -100, 100);
      hemisphereLightFolder.add(hemisphereLight.position, 'z', -100, 100);
      hemisphereLightFolder.add(hemisphereLight, 'intensity', 0, 10);
    }

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    /**
     * @description 创建地球
     */
    this.earthMesh = this.createEarth();
		// 这个球体将作为地球的基础层，后续的点状地图将建立在此之上
		this.scene.add(this.earthMesh);
    /**
     * @description 添加鼠标事件监听
     */
    this.addMouseListeners();
    /**
     * @description 创建 ResizeObserver 监听容器大小变化
     */
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.renderDomElement);
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * 创建地球
   * @returns 地球网格对象
   */
  private createEarth() {
		// 分段数越高球体越平滑，但会消耗更多性能
		const baseSphere = new THREE.SphereGeometry(
      /* 19.5 - 球体半径 */
      19.5,
      /* 35 - 水平分段数（经度） */
      35,
      /* 35 - 垂直分段数（纬度） */
      35
    );
		// 创建基础材质
		// MeshStandardMaterial 是基于物理的材质，可以更真实地反应光照
		const baseMaterial = new THREE.MeshStandardMaterial({
      /* 设置深蓝色作为基础色 */
			color: this.options.material?.color || 0x162a4e,
      /* 启用透明度 */
			transparent: this.options.material?.transparent || true,
      /* 设置90%的不透明度，让球体略微透明 */
			opacity: this.options.material?.opacity || 0.9,
      /* 添加这行 - 控制表面粗糙度 */
			// roughness: 0.5,
      /* 添加这行 - 控制金属感 */
			// metalness: 0.5,
		});

		// 创建网格对象
		// 将几何体和材质结合，形成可渲染的 3D 对象
		const earthMesh = new THREE.Mesh(baseSphere, baseMaterial);

    return earthMesh;
  }

  private handleMouseDown = (event: MouseEvent | TouchEvent) => {
    // 获取鼠标/触摸位置
    const pos = this.getEventPosition(event);

    // 更新鼠标位置
    this.mouse.x = (pos.x / this.width) * 2 - 1;
    this.mouse.y = -(pos.y / this.height) * 2 + 1;
    // 更新射线
    this.raycaster.setFromCamera(this.mouse, this.camera);

    // 检测射线与球体的交点
    const intersects = this.raycaster.intersectObject(this.earthMesh);

    // 如果有交点，说明点击到了球体
    if (intersects.length > 0) {
      if (this.dots) {
        this.dots.onMouseDown();
      }
    }
  }

  private handleMouseUp = () => {
    if (this.dots) {
      this.dots.onMouseUp();
    }
  }

  /**
   * 获取事件位置
   */
  private getEventPosition(event: MouseEvent | TouchEvent) {
    const rect = this.renderDomElement.getBoundingClientRect();

    if (event instanceof MouseEvent) {
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    } else {
      const touch = event.touches[0] || event.changedTouches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    }
  }

  /**
   * 添加鼠标事件监听
   */
  private addMouseListeners(): void {
    // 鼠标事件
    this.renderDomElement.addEventListener('mousedown', this.handleMouseDown);
    this.renderDomElement.addEventListener('mouseup', this.handleMouseUp);
    // 添加鼠标移出事件
    this.renderDomElement.addEventListener('mouseleave', this.handleMouseUp);

    // 触摸事件
    this.renderDomElement.addEventListener('touchstart', this.handleMouseDown, { passive: true });
    this.renderDomElement.addEventListener('touchend', this.handleMouseUp);
    // 添加触摸取消事件
    this.renderDomElement.addEventListener('touchcancel', this.handleMouseUp);
  }

  render(
    // starryOptions?: Partial<StarryOptions>,
    dotsOptions?: Partial<DotsOptions>,
    flylineOptions?: Partial<FlylineOptions>
  ): void {
    /**
     * @description 初始化星空
     */
    // this.starry = new Starry(this.scene, starryOptions);
    // this.starry.init();

    /**
     * @description 初始化点状地图
     */
    this.dots = new Dots(this.scene, dotsOptions);
    this.dots.init();

    /**
     * @description 初始化航线
     */
    this.flyline = new Flyline(this.scene, {
      cityList,
      bizLines,
      ...flylineOptions
    });
    this.flyline.init();

    /**
     * @description 开始动画循环
     */
    this.animate();
  }

  private animate(): void {
    const time = Date.now();

    // 更新控制器
    this.controls.update();
    // 更新星空
    // this.starry?.update(time);
    // 更新点状地图
    this.dots?.update();
    // 更新飞线
    this.flyline?.update(time);
    // 渲染场景
    this.renderer.render(this.scene, this.camera);
    // 继续动画循环
    requestAnimationFrame(() => this.animate());
  }

  /**
   * 处理大小变化
   */
  private handleResize = () => {
    // 更新宽高
    this.width = this.renderDomElement.clientWidth;
    this.height = this.renderDomElement.clientHeight;

    // 更新相机
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();

    // 更新渲染器
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // 如果有 LineMaterial，需要更新其分辨率
    if (this.flyline) {
      this.flyline.updateResolution(this.width, this.height);
    }
  }

  /**
   * 销毁地球场景
   */
  public dispose(): void {
    // 移除事件监听
    this.removeMouseListeners();
    // 销毁星空
    // this.starry?.dispose();
    // 销毁点状地图
    this.dots?.dispose();
    // 销毁飞线
    this.flyline?.dispose();
    // 移除 resize 监听
    this.resizeObserver.disconnect();
    // 清理 Raycaster
    // @ts-expect-error clean its reference
    this.raycaster = null;
    // @ts-expect-error clean its reference
    this.mouse = null;
    // @ts-expect-error clean its reference
    this.earthMesh = null;
    // clean resize event listener
    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * 移除鼠标事件监听
   */
  private removeMouseListeners(): void {
    // 鼠标事件
    this.renderDomElement.removeEventListener('mousedown', this.handleMouseDown);
    this.renderDomElement.removeEventListener('mouseup', this.handleMouseUp);
    this.renderDomElement.removeEventListener('mouseleave', this.handleMouseUp);

    // 触摸事件
    this.renderDomElement.removeEventListener('touchstart', this.handleMouseDown);
    this.renderDomElement.removeEventListener('touchend', this.handleMouseUp);
    this.renderDomElement.removeEventListener('touchcancel', this.handleMouseUp);
  }
}
